import React, { useEffect, useState } from 'react';
import config from '../config';
import { Link, useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

import { FileManager } from "@cubone/react-file-manager";
import "@cubone/react-file-manager/dist/style.css";

const BookBuilder = ({ testMode = false }) => {

    // const host = testMode ? config.testBackendUrl : config.backendUrl;
    const host = config.backendUrl;
    const productId = useParams().productId;

    const [product, setProduct] = useState(null);
    const [authToken, setAuthToken] = useState('');
    const [loading, setLoading] = useState(true);
    const [files, setFiles] = useState(null);
    const [folderChain, setFolderChain] = useState([{ id: 'root', name: 'Root', isDirectory: true, path: '/' }]);
    const [currentPath, setCurrentPath] = useState(null);
    const [fileLoading, setFileLoading] = useState(false);


    useEffect(() => {
        const getAuthToken = async () => {
            try {
                const session = await fetchAuthSession();
                const token = session.tokens.idToken.toString();
                setAuthToken(token);
            } catch (error) {
                console.error('Error getting auth token:', error);
            }
        };
        
        getAuthToken();
    }, []);


    useEffect(() => {
        if (!authToken || !productId) return; // Don't fetch if no token or productId yet

        setLoading(true);
        fetch(`${host}/api/products/${productId}`, {
            headers: {
                "X-API-KEY": process.env.REACT_APP_GANDF_KEY,
                'Content-Type': 'application/json',
            }
        })
        .then(response => {
            if (!response.ok) {
                console.error('Network response was not ok', response);
            }
            return response.json();
        })
        .then(data => {
            setProduct(data);
            setCurrentPath(data.s3_folder)
            setLoading(false);
        })
        .catch(error => {
            console.error('Error fetching product details:', error);
            setLoading(false);
        });
    }, [authToken, productId]);

    const fetchFiles = async (path = '/') => {
        if (!authToken || !productId) return;

        setFileLoading(true);
        try {
            console.log(path);
            const response = await fetch(`${host}/api/admin/products/${productId}/files?product_path=${encodeURIComponent(path)}&shallow=true`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch files');
            }

            const data = await response.json();
            setFiles(data.files || []);
            console.log(data.files)
        } catch (error) {
            console.error('Error fetching files:', error);
            alert('Error loading files');
        } finally {
            setFileLoading(false);
        }
    };

    useEffect(() => {
        if (authToken && productId && currentPath) {
            fetchFiles(currentPath);
        }
    }, [authToken, productId, currentPath]);

    const CustomImagePreviewer = ({ file }) => {
        const [filePreview, setFilePreview] = useState(null);

        useEffect(() => {

            async function fetchPreviewImage() {
                if (!authToken || !product) {
                    console.error("No auth token or product available");
                    return;
                }

                try {
                    console.log(file['path'])
                    const response = await fetch(`${host}/api/admin/products/${product.id}/file?product_path=${encodeURI(file['path'])}`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authToken}`
                        },
                    });

                    if (!response.ok) {
                        console.error('Error saving product:', response.statusText);
                        return;
                    }

                    const data = await response.json();

                    setFilePreview(data.url || null);
                } catch (error) {
                    console.error('Error loading preview:', error);
                }
            }

            fetchPreviewImage();
        }, [authToken, product])

        return (
            filePreview && <img src={`${filePreview}`} alt={file.name} />
        );
    };

    const handleSave = async () => {
        if (!authToken || !product) {
            console.error('No auth token or product available');
            return;
        }

        try {
            const response = await fetch(`${host}/api/admin/products/${product.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(product)
            });
            
            if (!response.ok) {
                console.error('Error saving product:', response.statusText);
                return;
            }
            alert('Product saved successfully!');
        } catch (error) {
            console.error('Error saving product:', error);
        }
    }


    const handleFileUpload = async (file, path) => {
        if (!authToken || !productId) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', path);

        try {
            const response = await fetch(`${host}/api/admin/products/${productId}/files/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to upload file');
            }

            // Refresh file list
            await fetchFiles(currentPath);
            alert('File uploaded successfully!');
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Error uploading file');
        }
    };

    // Handle folder creation
    const handleCreateFolder = async (folderName, parentFolder) => {
    
        // folders don't exist in S3, so just add the directory to the react state 
        setFiles((prevFiles) => [
            ...prevFiles,
            {
                name: folderName,
                isDirectory: true,
                path: `${parentFolder.path}/${folderName}`
            }
        ]);
    };


    // Handle file/folder deletion
    const handleDelete = async (fileList) => {
        if (!authToken || !productId) return;

        try {
            const response = await fetch(`${host}/api/admin/products/${productId}/files`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ paths: fileList  })
            });

            if (!response.ok) {
                throw new Error('Failed to delete file');
            }

            // Refresh file list
            await fetchFiles(currentPath);
        } catch (error) {
            console.error('Error deleting file:', error);
            alert('Error deleting file');
        }
    };

    // Handle file open (navigate to folder or view file)
    const handleFileOpen = (file) => {
        if (file.isDirectory) {
            setCurrentPath(file.path);
            // Update folder chain
            const pathParts = file.path.split('/').filter(p => p);
            const newChain = [{ id: 'root', name: 'Root', isDirectory: true, path: '/' }];
            let currentPathBuilder = '';
            pathParts.forEach((part, index) => {
                currentPathBuilder += '/' + part;
                newChain.push({
                    id: `folder-${index}`,
                    name: part,
                    isDirectory: true,
                    path: currentPathBuilder
                });
            });
            setFolderChain(newChain);
        } else {
            // Handle file opening (e.g., preview or download)
            console.log('Open file:', file);
            // You could open a preview modal or download the file
        }
    };

    const onRename = async (file, newName) => {
        if (!authToken || !productId) return;

        try {
            const response = await fetch(`${host}/api/admin/products/${productId}/files/move`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    productPath: currentPath.startsWith('/') ? currentPath.substring(1) : currentPath,
                    oldFileName: file.name,
                    newFileName: newName
                })
            });

            if (!response.ok) {
                throw new Error('Failed to rename file');
            }

            // Refresh file list
            await fetchFiles(currentPath);
        } catch (error) {
            console.error('Error renaming file:', error);
            alert('Error renaming file');
        }
    };

    if (loading) {
        return (
            <div className="main-content">
                <div style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
                    <div className="spinner" style={{ margin: '0 auto' }}></div>
                    <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)' }}>
                        Loading product details...
                    </p>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="main-content">
                <div className="card">
                    <div className="card-body" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
                        <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>❌</div>
                        <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Product Not Found</h3>
                        <p style={{ color: 'var(--text-secondary)' }}>
                            The product you're looking for doesn't exist or has been removed.
                        </p>
                        <Link to={testMode ? "/test/products" : "/products"} className="btn btn-primary">
                            Back to Products
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-content">
            {/* Page Header */}
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-header-title">
                        Book Builder
                        {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
                    </h1>
                </div>
                <div className="page-header-actions">
                    <Link to={testMode ? "/test/products" : "/products"} className="btn btn-secondary">
                        ← Back to Products
                    </Link>
                    <button className="btn btn-primary" onClick={handleSave}>
                        Save Changes
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">AI Book Creation</h3>
                </div>

                <div className="card-body">
                    <p>
                        Upload a folder containing all assets organized by page. Within the parent folder, include a text file named "instructions.txt" that outlines the rules and guidelines for generating each page of the book. Each page should have its own subfolder containing images and any additional text files needed for that page.
                    </p>
                    <input type="file" webkitdirectory="true" directory="true" multiple onChange={(e) => {
                        const files = e.target.files;
                        console.log(files);
                    }} />

                </div>
            </div>

            <br />

            <div className="table-actions">
                <button className="btn" onClick={() => {
                    setProduct({
                        ...product,
                        pages: [...(product.pages || []), { images: [], rules: [], text: [], num: product.pages ? product.pages.length + 1 : 0 }]
                    })
                }}> + Add New Page</button>
            </div>

            {/* Product Information */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Book Layout</h3>
                </div>
                <div className="card-body">
                    {/* List page links for each page in product.pages */}
                    {product.pages && product.pages.length > 0 ? (
                        <ul>
                            {product.pages.map((page, index) => (
                                <li key={index}>
                                    <Link to={testMode ? `/test/book-builder/${product.id}/page/${index}` : `/book-builder/${product.id}/page/${index}`} className="btn btn-link">
                                        Page {index + 1}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p>No pages found.</p>
                    )}
                </div>
            </div>

            <br/>

            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">Character Builder</h3>
                </div>
                <div className="card-body">
                    <Link to={testMode ? `/test/character-builder/${product.id}` : `/character-builder/${product.id}`} className="btn btn-primary">
                        Go to Character Builder
                    </Link>
                </div>
            </div>


            {files &&
                <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
                    <div className="card-header">
                        <h3 className="card-title">File Manager</h3>
                    </div>
                    <div className="card-body" style={{ height: '500px' }}>
                        <FileManager
                            files={files}
                            layout="list"
                            filePreviewComponent={(file) => <CustomImagePreviewer file={file} />}
                            onFileOpen={(file) => {
                                if (file.isDirectory) {
                                    // Handle folder navigation
                                    console.log('Navigate to folder:', file);
                                } else {
                                    // Handle file opening
                                    console.log('Open file:', file);
                                }

                            }}
                            onRefresh={() => fetchFiles(currentPath)}
                            onCreateFolder={(folderName, parentFolder) => handleCreateFolder(folderName, parentFolder)}
                            onFolderChange={setCurrentPath}
                            onFileUploaded={(file) => {
                                setFiles((prevFiles) => [...prevFiles, file]);
                            }}
                            onFileUploading={(file, parentFolder) => console.log(file, parentFolder)}
                            fileUploadConfig={{
                                url: `${host}/api/admin/products/${productId}/files/upload?path=${encodeURI(currentPath.startsWith('/') ? currentPath.substring(1) : currentPath)}`,
                                method: "POST",
                                headers: {
                                    'Authorization': `Bearer ${authToken}`
                                },
                            }}
                            onDelete={(fileList) => handleDelete(fileList.map(f => f.path.startsWith('/') ? f.path.substring(1) : f.path))}
                            onRename={(file, newName) => {
                                onRename(file, newName);
                            }}
                            isLoading={fileLoading}
                        />
                    </div>
                </div>
            }
        </div>
    );
}

export default BookBuilder;